import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  setDoc,
  query, 
  where, 
  orderBy,
  onSnapshot 
} from 'firebase/firestore';
import { db } from './firebase';

// Generic CRUD operations for Firestore
export const firestoreService = {
  // Create a new document
  async create(collectionName, data) {
    try {
      const docRef = await addDoc(collection(db, collectionName), {
        ...data,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating document:', error);
      throw error;
    }
  },

  // Set a document with a specific ID (creates or overwrites)
  async setWithId(collectionName, id, data, options = {}) {
    try {
      const docRef = doc(db, collectionName, id);
      
      // Check if document exists to determine if this is create or update
      const docSnap = await getDoc(docRef);
      const isUpdate = docSnap.exists();
      
      const docData = {
        ...data,
        updatedAt: new Date()
      };
      
      // Only add createdAt for new documents
      if (!isUpdate) {
        docData.createdAt = new Date();
      }
      
      // Use merge option to preserve existing fields during updates
      await setDoc(docRef, docData, { merge: options.merge !== false });
      return id;
    } catch (error) {
      console.error(`[Firestore] Error setting document ${id}:`, error);
      console.error('Error details:', error.message, error.code);
      throw error;
    }
  },

  // Read all documents from a collection
  async getAll(collectionName) {
    try {
      const querySnapshot = await getDocs(collection(db, collectionName));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting documents:', error);
      throw error;
    }
  },

  // Read a single document
  async getById(collectionName, id) {
    try {
      const docRef = doc(db, collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting document:', error);
      throw error;
    }
  },

  // Update a document
  async update(collectionName, id, data) {
    try {
      console.log(`[Firestore] Updating document ${id} in ${collectionName} with:`, data);
      const docRef = doc(db, collectionName, id);

      // Check if document exists first
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error(`Document ${id} does not exist in collection ${collectionName}`);
      }

      await updateDoc(docRef, {
        ...data,
        updatedAt: new Date()
      });

      console.log(`[Firestore] Successfully updated document ${id}`);
      return true;
    } catch (error) {
      console.error(`[Firestore] Error updating document ${id}:`, error);
      console.error('Error details:', error.message, error.code);
      throw error;
    }
  },

  // Delete a document
  async delete(collectionName, id) {
    try {
      await deleteDoc(doc(db, collectionName, id));
      return true;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  },

  // Query documents with conditions
  async query(collectionName, conditions = [], orderByField = null) {
    try {
      const collectionRef = collection(db, collectionName);
      const constraints = [];

      // Build where constraints - handle both array and object formats
      conditions.forEach(condition => {
        if (Array.isArray(condition)) {
          // Array format: ['field', 'operator', 'value']
          const [field, operator, value] = condition;
          constraints.push(where(field, operator, value));
        } else if (condition && typeof condition === 'object') {
          // Object format: {field, operator, value}
          constraints.push(where(condition.field, condition.operator, condition.value));
        }
      });

      // Add ordering constraint if specified
      if (orderByField) {
        constraints.push(orderBy(orderByField));
      }

      // Build query with all constraints at once
      const q = constraints.length > 0
        ? query(collectionRef, ...constraints)
        : collectionRef;

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error querying documents:', error);
      throw error;
    }
  },

  // Subscribe to real-time updates
  subscribe(collectionName, callback, conditions = [], onError) {
    const reportError = error => {
      if (onError) onError(error);
      else callback([], { fromCache: true });
    };
    try {
      const collectionRef = collection(db, collectionName);
      const constraints = [];

      // Build where constraints - handle both array and object formats
      conditions.forEach(condition => {
        if (Array.isArray(condition)) {
          // Array format: ['field', 'operator', 'value']
          const [field, operator, value] = condition;
          constraints.push(where(field, operator, value));
        } else if (condition && typeof condition === 'object') {
          // Object format: {field, operator, value}
          constraints.push(where(condition.field, condition.operator, condition.value));
        }
      });

      // Build query with all constraints at once
      const q = constraints.length > 0
        ? query(collectionRef, ...constraints)
        : collectionRef;

      return onSnapshot(
        q,
        { includeMetadataChanges: true },
        (querySnapshot) => {
          try {
            const docs = querySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            // Pass both data and metadata to callback
            callback(docs, querySnapshot.metadata);
          } catch (err) {
            console.error(`[firestoreService] Error processing snapshot for ${collectionName}:`, err);
            reportError(err);
          }
        },
        (error) => {
          console.error(`[firestoreService] Subscription error for ${collectionName}:`, error);
          reportError(error);
        }
      );
    } catch (error) {
      console.error(`[firestoreService] CRITICAL: Error setting up subscription to ${collectionName}:`, error);
      reportError(error);
      return () => {}; // Return no-op unsubscribe
    }
  }
};
